import { type ButtonHTMLAttributes, type ComponentType, type SVGProps } from 'react';
import { cn } from '@tg-hookah/ui';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

interface HeroCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  subtitle?: string;
  variant?: 'primary' | 'secondary';
  /** Background photograph — rendered in grayscale with a heavy scrim. */
  image?: string;
  icon?: LucideIcon;
  /** Short CTA label shown inside a small chip. */
  cta?: string;
  /** Analytics label emitted on click (via the global `data-track` listener). */
  track?: string;
}

/**
 * Hero = editorial-scale tile for the two prime actions on Home.
 *
 * Primary is a matte-black masthead with a single ember ribbon leaking
 * from the top-right corner — a distant streetlamp through fog.
 * Secondary carries a photograph, desaturated to near-mono, with a
 * hairline frame and a small CTA chip.
 */
export function HeroCard({
  title,
  subtitle,
  variant = 'primary',
  image,
  icon: Icon,
  cta,
  className,
  track,
  ...rest
}: HeroCardProps) {
  const primary = variant === 'primary';

  return (
    <button
      type="button"
      data-track={track}
      className={cn(
        'group relative isolate overflow-hidden rounded-3xl text-left text-white',
        'border border-[var(--line-1)]',
        'h-[180px] p-4 flex',
        'transition-transform duration-300 ease-out active:scale-[0.98]',
        'shadow-[0_12px_36px_-18px_rgba(0,0,0,0.85)]',
        'rise-in',
        className,
      )}
      {...rest}
    >
      {/* Background — photograph (greyscaled) or a black composition. */}
      {image ? (
        <img
          src={image}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          style={{ filter: 'saturate(1.05) contrast(1.02) brightness(0.92)' }}
        />
      ) : null}

      {primary ? (
        <>
          {/* Matte black base */}
          <span
            aria-hidden
            className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, #131315 0%, #0a0a0b 70%)' }}
          />
          {/* Ember ribbon — the single accent in the whole app. */}
          <span
            aria-hidden
            className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl animate-ember-pulse"
            style={{
              background:
                'radial-gradient(closest-side, rgba(194, 58, 46, 0.42), transparent 70%)',
            }}
          />
          {/* Top hairline */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 8%, rgba(255,255,255,0.22) 50%, transparent 92%)',
            }}
          />
        </>
      ) : (
        <>
          {/* A desaturated photo gets a vignette to keep text readable. */}
          <span
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(10,10,11,0.15) 0%, rgba(10,10,11,0.75) 100%)',
            }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 8%, rgba(255,255,255,0.2) 50%, transparent 92%)',
            }}
          />
        </>
      )}

      {/* Subtle noise */}
      <span aria-hidden className="noise absolute inset-0 rounded-3xl" />

      {/* Content: icon pinned top; title/subtitle/chip block pinned to the
          bottom via mt-auto. justify-between mis-pinned the block on iOS Safari
          (chip floated mid-card with a gap below it, intermittently) — mt-auto
          is reliable, matching NeonTile. */}
      <div className="relative flex flex-col h-full w-full min-w-0">
        {Icon ? (
          <span
            className="inline-flex w-9 h-9 items-center justify-center rounded-xl self-start shrink-0"
            style={{
              border: '1px solid var(--line-2)',
              background: 'rgba(255,255,255,0.03)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
          >
            <Icon size={18} strokeWidth={1.5} className="text-white/90" />
          </span>
        ) : null}

        <div className="mt-auto flex flex-col gap-1 min-w-0 w-full">
          <div className="font-display text-[17px] leading-[1.08] tracking-[-0.015em] font-extrabold break-words hyphens-none">
            {title}
          </div>
          {subtitle ? (
            <div className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-white/55 truncate">
              {subtitle}
            </div>
          ) : null}
          {cta ? (
            <span
              className={cn(
                'neon-chip mt-2 inline-flex self-start items-center gap-1.5',
                'rounded-full px-3 py-1 font-mono text-[9.5px] uppercase tracking-[0.16em]',
              )}
              style={{
                border: '1px solid var(--line-2)',
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              {cta}
              <span aria-hidden>↗</span>
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}
